import React, { useState, useEffect, useCallback, useMemo } from 'react';
// eslint-disable-next-line no-unused-vars
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  FiActivity, FiDownload, FiPrinter, FiSearch, FiTrendingUp,
  FiTrendingDown, FiPercent, FiShield, FiDollarSign, FiBarChart2
} from 'react-icons/fi';
import {
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { colors } from '../../theme/colors';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { recordReads } from '../../utils/readTelemetry';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import { formatCurrency, formatNumber } from '../../utils/formatters';
import { addPrintBrand } from '../../utils/printBranding';

// eslint-disable-next-line no-unused-vars
const CHART_COLORS = [
  colors.chart.primary,
  colors.chart.secondary,
  colors.chart.tertiary,
  colors.chart.accent1,
  colors.chart.accent2,
  colors.chart.accent3,
  '#4A90D9',
  '#E67E22',
  '#2ECC71',
  '#9B59B6'
];

const STATUS_COLORS = {
  Good: { color: '#28A745', bg: '#E8F5E9' },
  Warning: { color: '#F57C00', bg: '#FFF3E0' },
  Critical: { color: '#DC3545', bg: '#FFEBEE' }
};

const METRIC_TARGETS = {
  currentRatio: { target: 2.0, warningMin: 1.5, label: 'Current Ratio' },
  quickRatio: { target: 1.0, warningMin: 0.7, label: 'Quick Ratio' },
  debtToEquity: { target: 1.5, warningMax: 2.0, label: 'Debt-to-Equity', inverse: true },
  grossMargin: { target: 40, warningMin: 25, label: 'Gross Margin %' },
  netMargin: { target: 15, warningMin: 8, label: 'Net Margin %' },
  workingCapital: { target: 0, warningMin: 0, label: 'Working Capital' }
};

const formatAmount = (amount) => {
  const dp = (typeof window !== 'undefined' && Number.isFinite(Number(window.__AMAL_DECIMAL_PLACES__)))
    ? Number(window.__AMAL_DECIMAL_PLACES__) : 2;
  if (!amount && amount !== 0) return dp > 0 ? '0.' + '0'.repeat(dp) : '0';
  return Number(amount).toLocaleString('en-PK', {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp
  });
};

const formatDateStr = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-PK', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const formatRatioValue = (value) => {
  if (value === null || value === undefined || isNaN(value)) return '0.00';
  return Number(value).toFixed(2);
};

const formatPercentValue = (value) => {
  if (value === null || value === undefined || isNaN(value)) return '0.0%';
  return `${Number(value).toFixed(1)}%`;
};

const getMetricStatus = (metricKey, value) => {
  const config = METRIC_TARGETS[metricKey];
  if (!config) return 'Good';

  if (metricKey === 'workingCapital') {
    if (value > 0) return 'Good';
    return 'Critical';
  }

  if (config.inverse) {
    // Lower is better (e.g., debt-to-equity)
    if (value <= config.target) return 'Good';
    if (value <= config.warningMax) return 'Warning';
    return 'Critical';
  } else {
    // Higher is better
    if (value >= config.target) return 'Good';
    if (value >= config.warningMin) return 'Warning';
    return 'Critical';
  }
};

const getVsTargetPercent = (metricKey, value) => {
  const config = METRIC_TARGETS[metricKey];
  if (!config || config.target === 0) {
    if (metricKey === 'workingCapital') return value > 0 ? 100 : -100;
    return 0;
  }
  return ((value - config.target) / Math.abs(config.target)) * 100;
};

const getBarColor = (metricKey, value) => {
  const status = getMetricStatus(metricKey, value);
  if (status === 'Good') return '#28A745';
  if (status === 'Warning') return '#F57C00';
  return '#DC3545';
};

const FinancialHealthReport = () => {
  const navigate = useNavigate();
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();

  // Data states
  const [subTransactions, setSubTransactions] = useState([]);
  const [financialYears, setFinancialYears] = useState([]);

  // Filter states
  const [selectedFYId, setSelectedFYId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // UI states
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [sortField, setSortField] = useState('metric');
  const [sortDirection, setSortDirection] = useState('asc');

  // Load financial years
  const loadFinancialYears = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const fyRef = collection(db, 'companies', activeCompany.id, 'financialYears');
      const q = query(fyRef, orderBy('startDate', 'desc'));
      const snapshot = await getDocs(q);
      recordReads(activeCompany.id, snapshot.size);
      const fys = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setFinancialYears(fys);

      if (activeFinancialYear?.id) {
        setSelectedFYId(activeFinancialYear.id);
      } else if (fys.length > 0) {
        setSelectedFYId(fys[0].id);
      }
    } catch (error) {
      console.error('Error loading financial years:', error);
    }
  }, [activeCompany, activeFinancialYear]);

  useEffect(() => {
    loadFinancialYears();

    // Set default date range
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    setDateFrom(firstDay.toISOString().split('T')[0]);
    setDateTo(today.toISOString().split('T')[0]);
  }, [loadFinancialYears]);

  // Load subTransactions
  const loadData = async () => {
    if (!activeCompany?.id || !selectedFYId) {
      return;
    }

    setLoading(true);
    setHasSearched(true);

    try {
      const subTransRef = collection(db, 'companies', activeCompany.id, 'subTransactions');
      const conditions = [
        where('financialYearId', '==', selectedFYId),
        where('status', '==', 'Posted')
      ];

      const q = query(subTransRef, ...conditions);
      // ACC-20: opening balances may still be Draft — every ledger report counts
      // them, so this one must too (small query: only this year's opening rows).
      const obQ = query(subTransRef,
        where('financialYearId', '==', selectedFYId),
        where('status', '==', 'Draft'),
        where('tranTypeCode', '==', 'OB'));
      const [snapshot, obSnap] = await Promise.all([getDocs(q), getDocs(obQ)]);
      recordReads(activeCompany.id, snapshot.size + obSnap.size);

      let allSubTransactions = [...snapshot.docs, ...obSnap.docs].map(d => ({ id: d.id, ...d.data() }))
        .filter(t => {
          if (!activeBranch || activeBranch.id === 'all') return true;
          return t.branchId === activeBranch.id;
        });

      // Sort by docDate descending
      allSubTransactions.sort((a, b) => (b.docDate || '').localeCompare(a.docDate || ''));

      setSubTransactions(allSubTransactions);
    } catch (error) {
      console.error('Error loading sub-transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filtered sub-transactions
  const filteredSubTransactions = useMemo(() => {
    let filtered = [...subTransactions];

    // Date range filter
    if (dateFrom) {
      filtered = filtered.filter(t => (t.docDate || '') >= dateFrom);
    }
    if (dateTo) {
      filtered = filtered.filter(t => (t.docDate || '') <= dateTo);
    }

    return filtered;
  }, [subTransactions, dateFrom, dateTo]);

  // Classify accounts by prefix and compute balances
  const accountBalances = useMemo(() => {
    const balances = {
      currentAssets: 0,
      inventory: 0,
      currentLiabilities: 0,
      totalLiabilities: 0,
      totalEquity: 0,
      revenue: 0,
      cogs: 0,
      totalExpenses: 0
    };

    // ACC-20: same rules as Balance Sheet / P&L —
    //   balance accounts (1/2/3) = everything from the year's opening up to "To";
    //   income/expense (4/5)     = only the chosen period;
    //   equity also carries this year's profit up to "To" (retained earnings).
    subTransactions.forEach(t => {
      const d = t.docDate || '';
      if (dateTo && d > dateTo) return;
      const accountCode = String(t.accountCode || '');
      const debit = Number(t.debitAmount) || 0;
      const credit = Number(t.creditAmount) || 0;
      if (accountCode.startsWith('4')) balances.totalEquity += (credit - debit);
      if (accountCode.startsWith('5')) balances.totalEquity -= (debit - credit);
      const isPnl = accountCode.startsWith('4') || accountCode.startsWith('5');
      if (isPnl && dateFrom && d < dateFrom) return;

      // Current assets: 11*
      if (accountCode.startsWith('11')) {
        // Inventory: 113*
        if (accountCode.startsWith('113')) {
          balances.inventory += (debit - credit);
        }
        balances.currentAssets += (debit - credit);
      }

      // Current liabilities: 21*
      if (accountCode.startsWith('21')) {
        balances.currentLiabilities += (credit - debit);
      }

      // Total liabilities: 2*
      if (accountCode.startsWith('2')) {
        balances.totalLiabilities += (credit - debit);
      }

      // Equity: 3*
      if (accountCode.startsWith('3')) {
        balances.totalEquity += (credit - debit);
      }

      // Revenue: 4*
      if (accountCode.startsWith('4')) {
        balances.revenue += (credit - debit);
      }

      // COGS / Expenses: 5*
      if (accountCode.startsWith('5')) {
        balances.cogs += (debit - credit);
        balances.totalExpenses += (debit - credit);
      }
    });

    return balances;
  }, [subTransactions, dateFrom, dateTo]);

  // Computed financial metrics
  const metrics = useMemo(() => {
    const { currentAssets, inventory, currentLiabilities, totalLiabilities, totalEquity, revenue, cogs, totalExpenses } = accountBalances;

    const currentRatio = currentLiabilities !== 0 ? currentAssets / currentLiabilities : 0;
    const quickRatio = currentLiabilities !== 0 ? (currentAssets - inventory) / currentLiabilities : 0;
    const debtToEquity = totalEquity !== 0 ? totalLiabilities / totalEquity : 0;
    const grossMargin = revenue !== 0 ? ((revenue - cogs) / revenue) * 100 : 0;
    const netMargin = revenue !== 0 ? ((revenue - totalExpenses) / revenue) * 100 : 0;
    const workingCapital = currentAssets - currentLiabilities;

    return { currentRatio, quickRatio, debtToEquity, grossMargin, netMargin, workingCapital };
  }, [accountBalances]);

  // Ratio gauge data for horizontal bar charts
  const ratioGaugeData = useMemo(() => {
    return [
      {
        name: 'Current Ratio',
        key: 'currentRatio',
        value: metrics.currentRatio,
        target: METRIC_TARGETS.currentRatio.target,
        color: getBarColor('currentRatio', metrics.currentRatio)
      },
      {
        name: 'Quick Ratio',
        key: 'quickRatio',
        value: metrics.quickRatio,
        target: METRIC_TARGETS.quickRatio.target,
        color: getBarColor('quickRatio', metrics.quickRatio)
      },
      {
        name: 'Debt-to-Equity',
        key: 'debtToEquity',
        value: metrics.debtToEquity,
        target: METRIC_TARGETS.debtToEquity.target,
        color: getBarColor('debtToEquity', metrics.debtToEquity)
      }
    ];
  }, [metrics]);

  // Monthly margin trend data
  const marginTrendData = useMemo(() => {
    const monthlyData = {};

    filteredSubTransactions.forEach(t => {
      const docDate = t.docDate || '';
      if (!docDate) return;
      const date = new Date(docDate);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const accountCode = String(t.accountCode || '');
      const debit = Number(t.debitAmount) || 0;
      const credit = Number(t.creditAmount) || 0;

      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = { revenue: 0, cogs: 0, totalExpenses: 0 };
      }

      if (accountCode.startsWith('4')) {
        monthlyData[monthKey].revenue += (credit - debit);
      }
      if (accountCode.startsWith('5')) {
        monthlyData[monthKey].cogs += (debit - credit);
        monthlyData[monthKey].totalExpenses += (debit - credit);
      }
    });

    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    return Object.keys(monthlyData)
      .sort()
      .map(key => {
        const d = monthlyData[key];
        const grossMargin = d.revenue !== 0 ? ((d.revenue - d.cogs) / d.revenue) * 100 : 0;
        const operatingMargin = d.revenue !== 0 ? ((d.revenue - d.totalExpenses * 0.85) / d.revenue) * 100 : 0;
        const netMargin = d.revenue !== 0 ? ((d.revenue - d.totalExpenses) / d.revenue) * 100 : 0;

        const [year, month] = key.split('-');
        const label = `${months[parseInt(month, 10) - 1]} ${year}`;

        return {
          period: key,
          label,
          grossMargin: Number(grossMargin.toFixed(1)),
          operatingMargin: Number(operatingMargin.toFixed(1)),
          netMargin: Number(netMargin.toFixed(1))
        };
      });
  }, [filteredSubTransactions]);

  // Monthly working capital trend
  const workingCapitalTrendData = useMemo(() => {
    const monthlyData = {};

    filteredSubTransactions.forEach(t => {
      const docDate = t.docDate || '';
      if (!docDate) return;
      const date = new Date(docDate);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const accountCode = String(t.accountCode || '');
      const debit = Number(t.debitAmount) || 0;
      const credit = Number(t.creditAmount) || 0;

      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = { currentAssets: 0, currentLiabilities: 0 };
      }

      if (accountCode.startsWith('11')) {
        monthlyData[monthKey].currentAssets += (debit - credit);
      }
      if (accountCode.startsWith('21')) {
        monthlyData[monthKey].currentLiabilities += (credit - debit);
      }
    });

    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    return Object.keys(monthlyData)
      .sort()
      .map(key => {
        const d = monthlyData[key];
        const workingCapital = d.currentAssets - d.currentLiabilities;
        const [year, month] = key.split('-');
        const label = `${months[parseInt(month, 10) - 1]} ${year}`;

        return {
          period: key,
          label,
          workingCapital
        };
      });
  }, [filteredSubTransactions]);

  // Table data
  const tableData = useMemo(() => {
    const rows = [
      {
        metric: METRIC_TARGETS.currentRatio.label,
        key: 'currentRatio',
        currentValue: formatRatioValue(metrics.currentRatio),
        rawValue: metrics.currentRatio,
        target: formatRatioValue(METRIC_TARGETS.currentRatio.target),
        vsTarget: getVsTargetPercent('currentRatio', metrics.currentRatio),
        status: getMetricStatus('currentRatio', metrics.currentRatio)
      },
      {
        metric: METRIC_TARGETS.quickRatio.label,
        key: 'quickRatio',
        currentValue: formatRatioValue(metrics.quickRatio),
        rawValue: metrics.quickRatio,
        target: formatRatioValue(METRIC_TARGETS.quickRatio.target),
        vsTarget: getVsTargetPercent('quickRatio', metrics.quickRatio),
        status: getMetricStatus('quickRatio', metrics.quickRatio)
      },
      {
        metric: METRIC_TARGETS.debtToEquity.label,
        key: 'debtToEquity',
        currentValue: formatRatioValue(metrics.debtToEquity),
        rawValue: metrics.debtToEquity,
        target: formatRatioValue(METRIC_TARGETS.debtToEquity.target),
        vsTarget: getVsTargetPercent('debtToEquity', metrics.debtToEquity),
        status: getMetricStatus('debtToEquity', metrics.debtToEquity)
      },
      {
        metric: METRIC_TARGETS.grossMargin.label,
        key: 'grossMargin',
        currentValue: formatPercentValue(metrics.grossMargin),
        rawValue: metrics.grossMargin,
        target: formatPercentValue(METRIC_TARGETS.grossMargin.target),
        vsTarget: getVsTargetPercent('grossMargin', metrics.grossMargin),
        status: getMetricStatus('grossMargin', metrics.grossMargin)
      },
      {
        metric: METRIC_TARGETS.netMargin.label,
        key: 'netMargin',
        currentValue: formatPercentValue(metrics.netMargin),
        rawValue: metrics.netMargin,
        target: formatPercentValue(METRIC_TARGETS.netMargin.target),
        vsTarget: getVsTargetPercent('netMargin', metrics.netMargin),
        status: getMetricStatus('netMargin', metrics.netMargin)
      },
      {
        metric: METRIC_TARGETS.workingCapital.label,
        key: 'workingCapital',
        currentValue: formatCurrency(metrics.workingCapital),
        rawValue: metrics.workingCapital,
        target: '> 0',
        vsTarget: metrics.workingCapital > 0 ? 100 : -100,
        status: getMetricStatus('workingCapital', metrics.workingCapital)
      }
    ];

    return rows;
  }, [metrics]);

  // Sorted table data
  const sortedTableData = useMemo(() => {
    const sorted = [...tableData];
    sorted.sort((a, b) => {
      let aVal, bVal;

      if (sortField === 'metric') {
        aVal = a.metric.toLowerCase();
        bVal = b.metric.toLowerCase();
      } else if (sortField === 'currentValue') {
        aVal = a.rawValue;
        bVal = b.rawValue;
      } else if (sortField === 'target') {
        aVal = a.target;
        bVal = b.target;
      } else if (sortField === 'vsTarget') {
        aVal = a.vsTarget;
        bVal = b.vsTarget;
      } else if (sortField === 'status') {
        const order = { Good: 0, Warning: 1, Critical: 2 };
        aVal = order[a.status] || 0;
        bVal = order[b.status] || 0;
      } else {
        aVal = a[sortField];
        bVal = b[sortField];
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [tableData, sortField, sortDirection]);

  // Sort handler
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIndicator = (field) => {
    if (sortField !== field) return '';
    return sortDirection === 'asc' ? ' \u2191' : ' \u2193';
  };

  // CSV Export
  const handleExport = () => {
    if (sortedTableData.length === 0) return;

    const selectedFY = financialYears.find(f => f.id === selectedFYId);

    let csv = 'Financial Health Report\n';
    csv += `Company: ${activeCompany?.companyName || ''}\n`;
    csv += `Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || ''}\n`;
    csv += `Period: ${dateFrom || 'All'} to ${dateTo || 'All'}\n`;
    csv += `Generated: ${new Date().toLocaleString()}\n\n`;

    csv += 'Account Balances\n';
    csv += `Current Assets,${formatAmount(accountBalances.currentAssets)}\n`;
    csv += `Inventory,${formatAmount(accountBalances.inventory)}\n`;
    csv += `Current Liabilities,${formatAmount(accountBalances.currentLiabilities)}\n`;
    csv += `Total Liabilities,${formatAmount(accountBalances.totalLiabilities)}\n`;
    csv += `Total Equity,${formatAmount(accountBalances.totalEquity)}\n`;
    csv += `Revenue,${formatAmount(accountBalances.revenue)}\n`;
    csv += `COGS,${formatAmount(accountBalances.cogs)}\n`;
    csv += `Total Expenses,${formatAmount(accountBalances.totalExpenses)}\n\n`;

    csv += 'Metric,Current Value,Target,vs Target %,Status\n';

    sortedTableData.forEach(row => {
      csv += `"${row.metric}","${row.currentValue}","${row.target}","${row.vsTarget.toFixed(1)}%","${row.status}"\n`;
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Financial_Health_${dateFrom || 'all'}_${dateTo || 'all'}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Print
  const handlePrint = () => {
    const selectedFY = financialYears.find(f => f.id === selectedFYId);
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Financial Health Report</title>
      <style>
        body{font-family:'Segoe UI',sans-serif;margin:30px;color:#1A1A1A;font-size:12px}
        h1{color:#800020;font-size:20px;margin-bottom:5px}
        .meta{color:#666;font-size:11px;margin-bottom:15px}
        .summary-grid{display:flex;gap:15px;margin-bottom:20px;flex-wrap:wrap}
        .summary-box{padding:12px 18px;background:#f9f9f9;border-radius:6px;border-left:3px solid #800020;min-width:120px}
        .summary-label{font-size:10px;color:#666;text-transform:uppercase;margin-bottom:4px}
        .summary-val{font-size:16px;font-weight:600}
        table{width:100%;border-collapse:collapse;margin-bottom:15px}
        th{background:#800020;color:white;padding:7px 10px;text-align:left;font-size:10px;text-transform:uppercase}
        th.right{text-align:right}
        td{padding:6px 10px;border-bottom:1px solid #eee;font-size:11px}
        td.right{text-align:right;font-family:'Consolas',monospace}
        .status-good{color:#28A745;font-weight:600}
        .status-warning{color:#F57C00;font-weight:600}
        .status-critical{color:#DC3545;font-weight:600}
        @media print{body{margin:15px}.summary-grid{page-break-inside:avoid}}
      </style></head><body>
      <h1>Financial Health Report</h1>
      <div class="meta">
        ${activeCompany?.companyName || 'Company'} |
        Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || ''} |
        Period: ${formatDateStr(dateFrom)} to ${formatDateStr(dateTo)}
      </div>
      <div class="summary-grid">
        <div class="summary-box"><div class="summary-label">Current Ratio</div><div class="summary-val">${formatRatioValue(metrics.currentRatio)}</div></div>
        <div class="summary-box"><div class="summary-label">Quick Ratio</div><div class="summary-val">${formatRatioValue(metrics.quickRatio)}</div></div>
        <div class="summary-box"><div class="summary-label">Debt-to-Equity</div><div class="summary-val">${formatRatioValue(metrics.debtToEquity)}</div></div>
        <div class="summary-box"><div class="summary-label">Gross Margin</div><div class="summary-val">${formatPercentValue(metrics.grossMargin)}</div></div>
        <div class="summary-box"><div class="summary-label">Net Margin</div><div class="summary-val">${formatPercentValue(metrics.netMargin)}</div></div>
        <div class="summary-box"><div class="summary-label">Working Capital</div><div class="summary-val">${formatAmount(metrics.workingCapital)}</div></div>
      </div>
      <table>
        <thead><tr>
          <th>Metric</th><th class="right">Current Value</th><th class="right">Target</th>
          <th class="right">vs Target %</th><th>Status</th>
        </tr></thead>
        <tbody>
          ${sortedTableData.map(row => `
            <tr>
              <td>${row.metric}</td>
              <td class="right">${row.currentValue}</td>
              <td class="right">${row.target}</td>
              <td class="right">${row.vsTarget.toFixed(1)}%</td>
              <td><span class="status-${row.status.toLowerCase()}">${row.status}</span></td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    </body></html>`);
    addPrintBrand(w);
    w.document.close();
    w.print();
  };

  // Custom chart tooltips
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <ChartTooltipBox>
          <ChartTooltipLabel>{label}</ChartTooltipLabel>
          {payload.map((entry, index) => (
            <ChartTooltipRow key={index}>
              <ChartTooltipDot style={{ background: entry.color }} />
              <span>{entry.name}: </span>
              <strong>{entry.name.includes('Margin') || entry.name.includes('%') ? `${Number(entry.value).toFixed(1)}%` : formatCurrency(entry.value)}</strong>
            </ChartTooltipRow>
          ))}
        </ChartTooltipBox>
      );
    }
    return null;
  };

  const MarginTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <ChartTooltipBox>
          <ChartTooltipLabel>{label}</ChartTooltipLabel>
          {payload.map((entry, index) => (
            <ChartTooltipRow key={index}>
              <ChartTooltipDot style={{ background: entry.color }} />
              <span>{entry.name}: </span>
              <strong>{Number(entry.value).toFixed(1)}%</strong>
            </ChartTooltipRow>
          ))}
        </ChartTooltipBox>
      );
    }
    return null;
  };

  const WCTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <ChartTooltipBox>
          <ChartTooltipLabel>{label}</ChartTooltipLabel>
          {payload.map((entry, index) => (
            <ChartTooltipRow key={index}>
              <ChartTooltipDot style={{ background: entry.color }} />
              <span>{entry.name}: </span>
              <strong>{formatCurrency(entry.value)}</strong>
            </ChartTooltipRow>
          ))}
        </ChartTooltipBox>
      );
    }
    return null;
  };

  return (
    <PageLayout pageTitle="Financial Health Report" breadcrumb="Reports / Financial Reports / Financial Health">
      {/* Filters */}
      <FiltersCard>
        <FiltersGrid>
          <FilterGroup>
            <Label>Financial Year</Label>
            <Select value={selectedFYId} onChange={e => setSelectedFYId(e.target.value)}>
              <option value="">Select Financial Year</option>
              {financialYears.map(fy => (
                <option key={fy.id} value={fy.id}>
                  {fy.yearName || fy.yearCode}
                  {fy.isActive ? ' (Active)' : ''}
                </option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Date From</Label>
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          </FilterGroup>

          <FilterGroup>
            <Label>Date To</Label>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
          </FilterGroup>
        </FiltersGrid>

        <ButtonGroup>
          <Button onClick={handleExport} disabled={!hasSearched || sortedTableData.length === 0}>
            <FiDownload /> Export CSV
          </Button>
          <Button onClick={handlePrint} disabled={!hasSearched || sortedTableData.length === 0}>
            <FiPrinter /> Print
          </Button>
          <Button $primary onClick={loadData} disabled={loading || !selectedFYId}>
            <FiSearch /> {loading ? 'Loading...' : 'Generate Report'}
          </Button>
        </ButtonGroup>
      </FiltersCard>

      {/* Summary Cards */}
      {hasSearched && !loading && (
        <SummaryCards>
          <SummaryCard>
            <SummaryIcon $color="#28A745"><FiShield /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Current Ratio</SummaryLabel>
              <SummaryValue style={{ color: getBarColor('currentRatio', metrics.currentRatio) }}>
                {formatRatioValue(metrics.currentRatio)}
              </SummaryValue>
            </SummaryInfo>
          </SummaryCard>

          <SummaryCard>
            <SummaryIcon $color={colors.chart.accent3}><FiActivity /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Quick Ratio</SummaryLabel>
              <SummaryValue style={{ color: getBarColor('quickRatio', metrics.quickRatio) }}>
                {formatRatioValue(metrics.quickRatio)}
              </SummaryValue>
            </SummaryInfo>
          </SummaryCard>

          <SummaryCard>
            <SummaryIcon $color="#DC3545"><FiBarChart2 /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Debt-to-Equity</SummaryLabel>
              <SummaryValue style={{ color: getBarColor('debtToEquity', metrics.debtToEquity) }}>
                {formatRatioValue(metrics.debtToEquity)}
              </SummaryValue>
            </SummaryInfo>
          </SummaryCard>

          <SummaryCard>
            <SummaryIcon $color={colors.chart.accent1}><FiPercent /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Gross Margin %</SummaryLabel>
              <SummaryValue style={{ color: getBarColor('grossMargin', metrics.grossMargin) }}>
                {formatPercentValue(metrics.grossMargin)}
              </SummaryValue>
            </SummaryInfo>
          </SummaryCard>

          <SummaryCard>
            <SummaryIcon $color={colors.chart.accent2}><FiTrendingUp /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Net Margin %</SummaryLabel>
              <SummaryValue style={{ color: getBarColor('netMargin', metrics.netMargin) }}>
                {formatPercentValue(metrics.netMargin)}
              </SummaryValue>
            </SummaryInfo>
          </SummaryCard>

          <SummaryCard>
            <SummaryIcon $color={colors.primary.main}><FiDollarSign /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Working Capital</SummaryLabel>
              <SummaryValue style={{ color: metrics.workingCapital >= 0 ? '#28A745' : '#DC3545' }}>
                {formatCurrency(metrics.workingCapital)}
              </SummaryValue>
            </SummaryInfo>
          </SummaryCard>
        </SummaryCards>
      )}

      {/* Data Table */}
      <ContentCard>
        <TableContainer>
          {loading ? (
            <EmptyState>
              <EmptyIcon><FiActivity /></EmptyIcon>
              <EmptyText>Loading financial health data...</EmptyText>
            </EmptyState>
          ) : !hasSearched ? (
            <EmptyState>
              <EmptyIcon><FiActivity /></EmptyIcon>
              <EmptyText>Select filters and click "Generate Report" to view financial health metrics</EmptyText>
            </EmptyState>
          ) : filteredSubTransactions.length === 0 ? (
            <EmptyState>
              <EmptyIcon><FiActivity /></EmptyIcon>
              <EmptyText>No sub-transactions found for the selected criteria</EmptyText>
            </EmptyState>
          ) : (
            <Table>
              <Thead>
                <Tr>
                  <Th $sortable onClick={() => handleSort('metric')}>Metric{getSortIndicator('metric')}</Th>
                  <Th $sortable $right onClick={() => handleSort('currentValue')}>Current Value{getSortIndicator('currentValue')}</Th>
                  <Th $sortable $right onClick={() => handleSort('target')}>Target{getSortIndicator('target')}</Th>
                  <Th $sortable $right onClick={() => handleSort('vsTarget')}>vs Target %{getSortIndicator('vsTarget')}</Th>
                  <Th $sortable onClick={() => handleSort('status')}>Status{getSortIndicator('status')}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {sortedTableData.map((row, index) => (
                  <Tr key={row.key || index}>
                    <Td>
                      <MetricName>{row.metric}</MetricName>
                    </Td>
                    <Td $right>
                      <MetricValue>{row.currentValue}</MetricValue>
                    </Td>
                    <Td $right>
                      <MetricValue>{row.target}</MetricValue>
                    </Td>
                    <Td $right>
                      <VsTargetValue $positive={row.vsTarget >= 0}>
                        {row.vsTarget >= 0 ? <FiTrendingUp size={12} /> : <FiTrendingDown size={12} />}
                        {row.vsTarget >= 0 ? '+' : ''}{row.vsTarget.toFixed(1)}%
                      </VsTargetValue>
                    </Td>
                    <Td>
                      <StatusBadge $status={row.status}>
                        {row.status}
                      </StatusBadge>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </TableContainer>
      </ContentCard>

      {/* Charts */}
      {hasSearched && !loading && filteredSubTransactions.length > 0 && (
        <ChartsRow>
          {/* Ratio Gauges - Horizontal Bar Charts */}
          <ChartSection>
            <ChartTitle>Financial Ratios vs Targets</ChartTitle>
            {ratioGaugeData.length > 0 ? (
              <div>
                {ratioGaugeData.map((gauge, idx) => (
                  <GaugeRow key={idx}>
                    <GaugeLabel>{gauge.name}</GaugeLabel>
                    <GaugeBarWrapper>
                      <ResponsiveContainer width="100%" height={36}>
                        <BarChart
                          data={[gauge]}
                          layout="vertical"
                          margin={{ top: 2, right: 40, left: 0, bottom: 2 }}
                        >
                          <XAxis
                            type="number"
                            domain={[0, Math.max(gauge.value * 1.3, gauge.target * 1.3, 1)]}
                            hide
                          />
                          <YAxis type="category" dataKey="name" hide />
                          <Bar
                            dataKey="value"
                            fill={gauge.color}
                            radius={[0, 4, 4, 0]}
                            barSize={18}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                      <TargetLine
                        style={{
                          left: `${Math.min((gauge.target / Math.max(gauge.value * 1.3, gauge.target * 1.3, 1)) * 100, 95)}%`
                        }}
                      />
                    </GaugeBarWrapper>
                    <GaugeValue style={{ color: gauge.color }}>
                      {formatRatioValue(gauge.value)}
                      <GaugeTarget>/ {formatRatioValue(gauge.target)}</GaugeTarget>
                    </GaugeValue>
                  </GaugeRow>
                ))}
              </div>
            ) : (
              <ChartEmpty>No ratio data available</ChartEmpty>
            )}
          </ChartSection>

          {/* Margin Trend Area Chart */}
          <ChartSection>
            <ChartTitle>Margin Trend (%)</ChartTitle>
            {marginTrendData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={marginTrendData} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="grossMarginGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#28A745" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#28A745" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="operatingMarginGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={colors.chart.accent3} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={colors.chart.accent3} stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="netMarginGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={colors.primary.main} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={colors.primary.main} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.neutral[200]} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke={colors.text.secondary} />
                  <YAxis tick={{ fontSize: 11 }} stroke={colors.text.secondary} tickFormatter={(v) => `${v}%`} />
                  <Tooltip content={<MarginTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Area type="monotone" dataKey="grossMargin" name="Gross Margin %" stroke="#28A745" fill="url(#grossMarginGrad)" strokeWidth={2} />
                  <Area type="monotone" dataKey="operatingMargin" name="Operating Margin %" stroke={colors.chart.accent3} fill="url(#operatingMarginGrad)" strokeWidth={2} />
                  <Area type="monotone" dataKey="netMargin" name="Net Margin %" stroke={colors.primary.main} fill="url(#netMarginGrad)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <ChartEmpty>No margin trend data available</ChartEmpty>
            )}
          </ChartSection>

          {/* Working Capital Trend Bar Chart */}
          <ChartSection $wide>
            <ChartTitle>Working Capital Trend (Monthly)</ChartTitle>
            {workingCapitalTrendData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={workingCapitalTrendData} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.neutral[200]} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke={colors.text.secondary} />
                  <YAxis tick={{ fontSize: 11 }} stroke={colors.text.secondary} tickFormatter={(v) => formatNumber(v, { compact: true })} />
                  <Tooltip content={<WCTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar
                    dataKey="workingCapital"
                    name="Working Capital"
                    radius={[4, 4, 0, 0]}
                    barSize={32}
                  >
                    {workingCapitalTrendData.map((entry, index) => (
                      <rect
                        key={`cell-${index}`}
                        fill={entry.workingCapital >= 0 ? '#28A745' : '#DC3545'}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <ChartEmpty>No working capital trend data available</ChartEmpty>
            )}
          </ChartSection>
        </ChartsRow>
      )}
    </PageLayout>
  );
};

// ============ STYLED COMPONENTS ============

const FiltersCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  margin-bottom: 20px;
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 14px;
  margin-bottom: 16px;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const Select = styled.select`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  background: white;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const Input = styled.input`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 9px 18px;
  border: ${props => props.$primary ? 'none' : `1px solid ${colors.neutral[300]}`};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 7px;
  transition: all 0.2s;

  ${props => props.$primary ? `
    background: ${colors.primary.main};
    color: white;
    &:hover { background: ${colors.primary.dark}; }
  ` : `
    background: white;
    color: ${colors.text.primary};
    &:hover { background: ${colors.neutral[100]}; border-color: ${colors.primary.main}; }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SummaryCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
`;

const SummaryCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SummaryIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${colors.borderRadius.md};
  background: ${props => props.$color || colors.primary.main}15;
  color: ${props => props.$color || colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`;

const SummaryInfo = styled.div`
  min-width: 0;
`;

const SummaryLabel = styled.div`
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-bottom: 4px;
  white-space: nowrap;
`;

const SummaryValue = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: ${colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ChartsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const ChartSection = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;

  ${props => props.$wide && `
    grid-column: 1 / -1;
  `}
`;

const ChartTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin: 0 0 16px 0;
`;

const ChartEmpty = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: ${colors.text.tertiary};
  font-size: 14px;
`;

const ChartTooltipBox = styled.div`
  background: white;
  border: 1px solid ${colors.neutral[200]};
  border-radius: ${colors.borderRadius.md};
  padding: 10px 14px;
  box-shadow: ${colors.shadows.md};
`;

const ChartTooltipLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin-bottom: 6px;
`;

const ChartTooltipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-bottom: 2px;

  strong {
    color: ${colors.text.primary};
  }
`;

const ChartTooltipDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
`;

// Gauge components
const GaugeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const GaugeLabel = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: ${colors.text.secondary};
  min-width: 110px;
  flex-shrink: 0;
`;

const GaugeBarWrapper = styled.div`
  flex: 1;
  position: relative;
`;

const TargetLine = styled.div`
  position: absolute;
  top: 2px;
  bottom: 2px;
  width: 2px;
  background: ${colors.text.primary};
  z-index: 1;

  &::after {
    content: 'Target';
    position: absolute;
    top: -14px;
    left: 50%;
    transform: translateX(-50%);
    font-size: 9px;
    color: ${colors.text.tertiary};
    white-space: nowrap;
  }
`;

const GaugeValue = styled.div`
  font-size: 14px;
  font-weight: 600;
  min-width: 80px;
  text-align: right;
  display: flex;
  align-items: baseline;
  justify-content: flex-end;
  gap: 4px;
`;

const GaugeTarget = styled.span`
  font-size: 11px;
  font-weight: 400;
  color: ${colors.text.tertiary};
`;

const ContentCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  overflow: hidden;
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: ${colors.neutral[50]};
`;

const Th = styled.th`
  padding: 12px 14px;
  text-align: ${props => props.$right ? 'right' : 'left'};
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid ${colors.neutral[200]};
  white-space: nowrap;
  user-select: none;

  ${props => props.$sortable && `
    cursor: pointer;
    transition: color 0.2s;
    &:hover {
      color: ${colors.primary.main};
    }
  `}
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid ${colors.neutral[100]};
  transition: background 0.15s;

  &:hover {
    background: ${colors.neutral[50]};
  }
`;

const Td = styled.td`
  padding: 10px 14px;
  font-size: 13px;
  color: ${colors.text.primary};
  text-align: ${props => props.$right ? 'right' : 'left'};
`;

const MetricName = styled.span`
  font-weight: 500;
  color: ${colors.text.primary};
`;

const MetricValue = styled.span`
  font-family: 'SF Mono', 'Consolas', 'Courier New', monospace;
  font-size: 13px;
  color: ${colors.text.primary};
`;

const VsTargetValue = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: 'SF Mono', 'Consolas', 'Courier New', monospace;
  font-size: 13px;
  font-weight: 500;
  color: ${props => props.$positive ? '#28A745' : '#DC3545'};
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  background: ${props => STATUS_COLORS[props.$status]?.bg || colors.neutral[100]};
  color: ${props => STATUS_COLORS[props.$status]?.color || colors.text.secondary};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${colors.text.secondary};
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  color: ${colors.neutral[300]};
  margin-bottom: 16px;
`;

const EmptyText = styled.p`
  font-size: 16px;
  margin-bottom: 8px;
`;

export default FinancialHealthReport;
